import { FormControl, styled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useEffect, useId, useMemo, useState } from 'react';

import type { ScaAllowedValues } from '@data/global-api-payments';
import {
  LiveRegionAnnouncer,
  useTranslationsContext,
} from '@state/checkout-contexts';
import {
  ChkGenericError,
  ChkOptionButtonGroup,
  ChkOptionLabel,
} from '@ui/checkout-components';
import { UiButtonWithLoading } from '@ui/molecules-button-with-loading';
import { captureException, isNetworkError } from '@utility/checkout-utils';

import { HeaderWithCloseButton } from '../../components/HeaderWithCloseButton/HeaderWithCloseButton';
import { ViewContainer } from '../../components/ViewContainer/ViewContainer';
import { useCancelModal } from '../../hooks/useCancelModal/useCancelModal';
import { SCA_EVENTS } from '../../models/globals/events.constants';
import { TEST_IDS } from '../../models/globals/testIds.constants';
import { useScaSubject } from '../../providers/ScaSubjectProvider/ScaSubjectProvider.hooks';
import { dataLayer } from '../../utils/dataLayer';
import { getApiErrorCodeAndMessageKey } from '../../utils/getApiErrorCodeAndMessageKey';
import { getSCAMethods } from '../../utils/getSCAMethods';
import { TRANSLATION_KEYS } from '../../utils/TranslationKeys';

import {
  AUTHENTICATION_METHODS_PROPS,
  AUTHENTICATION_METHODS_VIEW_CANCELLATION_REASONS,
  AUTHENTICATION_METHODS_VIEW_NAME,
  AUTHENTICATION_METHODS_VIEW_SELECT_DISABLED_REASON_ID,
} from './AuthenticationMethodsView.constants';
import { transformGlobalApiScaMethodsForDataLayer } from './AuthenticationMethodsView.utils';

export const AuthenticationMethodsView = () => {
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const { t } = useTranslationsContext();
  const legendId = useId();

  const { subject, updateSubject, isFetching } = useScaSubject();
  const { modal, openModal } = useCancelModal({
    cancellationReasons: AUTHENTICATION_METHODS_VIEW_CANCELLATION_REASONS,
    screenName: AUTHENTICATION_METHODS_VIEW_NAME,
  });
  const scaMethods = getSCAMethods(subject);

  const options = useMemo(
    () =>
      scaMethods?.allowedValues?.map(({ name, value, description }) => {
        const { title, subtitle, icon } = AUTHENTICATION_METHODS_PROPS[name];
        return {
          value,
          label: (
            <ChkOptionLabel
              data-testid={TEST_IDS.AUTHENTICATION_VIEW[name].method}
              title={t(title)}
              description={description ?? t(subtitle)}
              icon={icon}
            />
          ),
        };
      }),
    [scaMethods?.allowedValues, t]
  );

  const handleMethodSelection = ({
    value: id,
    name: type,
    description,
  }: ScaAllowedValues) => {
    setSelectedMethodId(id);
    dataLayer.trackEvent({
      name: SCA_EVENTS.AUTHENTICATION_METHOD_SELECTED,
      properties: {
        scaMethod: {
          id,
          type,
          description,
        },
      },
    });
  };

  const handleSubmit = async () => {
    if (!selectedMethodId) {
      return null;
    }

    const { error } = await updateSubject({
      sca: {
        method: selectedMethodId,
      },
    });

    if (error !== null) {
      return handleError(error);
    }

    dataLayer.trackEvent({ name: SCA_EVENTS.SUBMIT_AUTHENTICATION_METHOD });
  };

  const handleError = (error: unknown) => {
    const { code } = getApiErrorCodeAndMessageKey(error);

    dataLayer.trackEvent({
      name: SCA_EVENTS.AUTHENTICATION_METHODS_PAGE_ERROR,
      properties: {
        code,
      },
    });

    if (!isNetworkError(error)) {
      captureException(error);
    }
  };

  const handleMethodChange = (value: string) => {
    const method = scaMethods?.allowedValues?.find((it) => it.value === value);
    if (method) {
      handleMethodSelection(method);
    }
  };

  useEffect(() => {
    const availableScaMethods = transformGlobalApiScaMethodsForDataLayer(
      scaMethods?.allowedValues
    );

    dataLayer.trackEvent({
      name: SCA_EVENTS.AUTHENTICATION_PAGE_DISPLAYED,
      properties: {
        scaMethods: availableScaMethods ?? [],
      },
    });
  }, [scaMethods]);

  if (!scaMethods || !options) {
    return (
      <ChkGenericError
        title={t(TRANSLATION_KEYS.SOMETHING_WENT_WRONG)}
        text={t(TRANSLATION_KEYS.OOPS_IT_APPEARS_THERE_IS_AN_ISSUE)}
      />
    );
  }

  const liveRegionSubmitMessage = isFetching
    ? t(TRANSLATION_KEYS.SUBMITTING_VERIFICATION_METHOD)
    : null;

  return (
    <ViewContainer>
      <header>
        <HeaderWithCloseButton
          onClick={openModal}
          aria-label={t(TRANSLATION_KEYS.OPEN_CANCEL_MODAL)}
          aria-haspopup={true}
        >
          {t(TRANSLATION_KEYS.AUTHENTICATION_METHOD)}
        </HeaderWithCloseButton>
      </header>
      <main>
        <FormControl component="fieldset" fullWidth>
          <MethodsContainer>
            <Legend id={legendId} style={visuallyHidden}>
              {t(TRANSLATION_KEYS.SELECT_AUTHENTICATION_METHOD)}
            </Legend>
            <ChkOptionButtonGroup
              selectedOption={selectedMethodId ?? ''}
              setSelectedOption={handleMethodChange}
              options={options}
              ariaLabelledby={legendId}
            />
          </MethodsContainer>
        </FormControl>
        <LiveRegionAnnouncer
          debounceDelay={500}
          message={liveRegionSubmitMessage}
        />
        <StyledButton
          variant="contained"
          onClick={handleSubmit}
          isLoading={isFetching}
          aria-disabled={!selectedMethodId}
          data-testid={TEST_IDS.SCA_AUTHENTICATION_METHODS_VIEW_CTA}
          aria-describedby={
            !selectedMethodId
              ? AUTHENTICATION_METHODS_VIEW_SELECT_DISABLED_REASON_ID
              : undefined
          }
        >
          {t(TRANSLATION_KEYS.CONTINUE)}
        </StyledButton>
        <span
          style={visuallyHidden}
          id={AUTHENTICATION_METHODS_VIEW_SELECT_DISABLED_REASON_ID}
        >
          {t(TRANSLATION_KEYS.SELECT_AUTHENTICATION_METHOD_DISABLED_REASON)}
        </span>
      </main>
      {modal}
    </ViewContainer>
  );
};

const Legend = styled('legend')({});

const MethodsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: theme.spacing(5),
  marginTop: theme.spacing(5),
  borderWidth: 0,
  padding: 0,
}));

const StyledButton = styled(UiButtonWithLoading)(({ theme }) => ({
  marginTop: theme.spacing(9),
  width: '100%',
}));
